import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useRouteMatch } from 'react-router-dom'

import { FullContainer, InsideContainer } from './styled-components'
import { makeZendeskRedirectLink } from '../../utils/getZendeskLinks'
import { useAuthStore } from '../../zustand/authentication/use-auth-store'

import LoginLogo from './components/login/login-logo'
import LoginForm from './components/login/login-form'
import Login2FAForm from './components/login/login-2fa-form'
import LoginFooterLinks from './components/login/login-footer-links'
import ResetPassword from './components/reset-password/reset-password'
import LoginAccessDenied from './components/login/login-access-denied.jsx'
import SnackbarComponent from '../../components/stories/molecules/snackbar/index.jsx'

import { useLoginMutations } from './components/hooks/use-login-handlers'
import { useModalState } from './hooks/use-modal-state'
import VersionInfo from './components/version-info/index.jsx'
import LoginStoreBanner from './components/login-banner/index.jsx'
import StaticBannerComponent from '../../components/stories/molecules/static-banner/index.jsx'
import { useIntl } from 'react-intl'
import { staticBannerVariantOptions } from '../../components/stories/molecules/static-banner/constants.js'

const Login = ({ isInPopup, hideLogo }) => {
  const {
    emailVerifying,
    handleLogin2faHook,
    handleLoginHook,
    handleLoginRedirect,
    handleVerifyEmail,
    isLoading,
    loginError,
    login2faError,
    resetLoginMutations
  } = useLoginMutations()

  // Extract URL parameters
  const query = new URLSearchParams(window.location?.search)
  const returnTo = query.get('return_to')
  const match = useRouteMatch()
  const isError = loginError || login2faError
  const params = match?.params

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')

  const [showSuccessBanner, setShowSuccessBanner] = useState(
    params && (params.mode === 'activated' || params.mode === 'updated')
  )

  const { formatMessage } = useIntl()

  const {
    isOpenResetPassword,
    isOpenLoginAccessDenied,
    handleToggleResetPassword,
    handleToggleAccessDenied
  } = useModalState()

  const { redirectToken, show2FA, setShow2FA } = useAuthStore()

  const handleCloseAccessDenied = () => {
    resetLoginMutations()
    handleToggleAccessDenied()
  }

  const handleBackToLogin = () => {
    setShow2FA(undefined)
  }

  // We don't need the keypress handler anymore as forms handle submission

  const handleLogin2fa = async (data) => {
    // Get the 2FA code from the form data
    const { twoFaCode } = data

    await handleLogin2faHook({
      email,
      password,
      twoFaCode
    })
  }

  const handleLogin = async (data) => {
    // Store email for reset password component
    setEmail(data.email)
    setPassword(data.password)

    // Handle login based on redirect requirement
    if (returnTo) {
      return handleLoginRedirect(data)
    }

    await handleLoginHook(data)
  }

  // Render helper functions for better organization
  const renderLoginForm = () => {
    if (show2FA || emailVerifying) return null

    return (
      <>
        <LoginForm
          handleLogin={handleLogin}
          isLoading={isLoading}
          isInPopup={isInPopup}
          emailVerifying={emailVerifying}
          onEmailChange={setEmail}
          defaultEmail={email}
        />
        <LoginFooterLinks
          isInPopup={isInPopup}
          openResetPassword={handleToggleResetPassword}
        />
        {!isInPopup && <VersionInfo />}
      </>
    )
  }

  const render2FAForm = () => {
    if (!show2FA) return null

    return (
      <Login2FAForm
        show2FA={show2FA}
        handleLogin={handleLogin2fa}
        handleBackToLogin={handleBackToLogin}
        error={login2faError}
        isLoading={isLoading}
        resetLoginMutations={resetLoginMutations}
      />
    )
  }

  const renderModals = () => (
    <>
      <ResetPassword
        email={email}
        isOpen={isOpenResetPassword}
        onClose={handleToggleResetPassword}
      />

      <LoginAccessDenied
        isOpen={isOpenLoginAccessDenied}
        onClose={handleCloseAccessDenied}
      />
    </>
  )

  // Handle email verification
  useEffect(() => {
    if (params?.mode === 'verifyEmail' && params?.token) {
      handleVerifyEmail({ token: params?.token })
    }
  }, [params, handleVerifyEmail])

  // Handle redirect token
  useEffect(() => {
    if (redirectToken) {
      window.location = makeZendeskRedirectLink(
        redirectToken.access_token,
        returnTo
      )
    }
  }, [redirectToken, returnTo])

  useEffect(() => {
    if (isError && isError?.response.data.error === 'User is disabled') {
      return handleToggleAccessDenied()
    }

    if (isError && show2FA) {
      setShow2FA(false)
    }
  }, [isError])

  useEffect(() => {
    if (showSuccessBanner) {
      const timer = setTimeout(() => {
        setShowSuccessBanner(false)
      }, 6000)

      return () => clearTimeout(timer)
    }
  }, [showSuccessBanner])

  return (
    <FullContainer isInPopup={isInPopup}>
      <div>
        <LoginStoreBanner show={!isInPopup} />
        <InsideContainer isInPopup={isInPopup} maxWidth={400}>
          <LoginLogo hideLogo={hideLogo} isInPopup={isInPopup} />

          {isError && !isOpenLoginAccessDenied && (
            <StaticBannerComponent
              message={formatMessage({ id: 'login_failed' })}
              variant={staticBannerVariantOptions.WARNING}
            />
          )}

          {showSuccessBanner && (
            <StaticBannerComponent
              message={
                params.mode === 'activated'
                  ? formatMessage({ id: 'registerForm_activationSuccessful' })
                  : formatMessage({
                      id: 'registerForm_resetPasswordSuccessful'
                    })
              }
              variant={staticBannerVariantOptions.SUCCESS}
            />
          )}

          {renderLoginForm()}

          {render2FAForm()}
        </InsideContainer>
      </div>

      {renderModals()}

      <SnackbarComponent style={{ top: '1.5rem', right: '1.5rem' }} />
    </FullContainer>
  )
}

Login.propTypes = {
  isInPopup: PropTypes.bool,
  hideLogo: PropTypes.bool
}

export default Login
